import React from "react";
import PropTypes from "prop-types";

import TherapyPicker from "modules/patient/components/TherapyPicker";
import PreferredLanguagesPopover from "modules/patient/components/PreferredLanguagesPopover";
import {
  PROFILE_TYPE_CHIP_CLASS,
  profileTypeLabel,
} from "modules/patient/helper/patientConstants";
import { formatAgeLabel, formatGenderAge } from "modules/patient/helper/patientFormatters";
import MOBILE from "../mobileConfig";

/**
 * Row 3 — who this patient is. Two lines, per the board (5330:2):
 *
 *   PID 10248 · Premium user · Heart health ▾
 *   Female · 52 yr · Original appointment — Vineeta Verma
 *
 * The therapy chip is the real `TherapyPicker`, so the row is a control, not a
 * readout. Editing the patient's own details moved to the pencil in row 1,
 * where the board puts it and where it sits beside the name it edits.
 *
 * PREFERRED LANGUAGES ARE HIDDEN HERE, not removed — the board has no line for
 * them and the popover is a desktop-width control. Desktop keeps it, and
 * MOBILE.showPreferredLanguages brings it back in one flip.
 */
const MobilePatientMeta = ({
  patient,
  condition,
  therapyTag,
  onTherapyChanged,
  languages,
  languagesState,
  onLanguagesSaved,
  originalBookingName,
}) => {
  if (!patient) return null;

  const typeLabel = profileTypeLabel(patient.profileType);
  const typeClass = PROFILE_TYPE_CHIP_CLASS[patient.profileType];
  const age = formatAgeLabel(patient.dateOfBirth, patient.age);
  const genderAge = formatGenderAge(patient.gender, age);

  return (
    <div className="zym-meta">
      <div className="zym-meta-line">
        {/* `ph-chip`, not a mobile class of its own. The user-type tones are a
            gradient and three hexes that patient.scss:196-258 lifted from two
            places that already colour-code this enum, with a recorded contrast
            note — restating any of that here would make a second source of
            truth for a colour the desktop header owns. Wearing the same class
            means the tone arrives with it. */}
        <span className="ph-chip">PID {patient.id}</span>
        {/* profileType is a classification enum, not a plan tier — all four
            classes show, and the default deliberately gets no chip. The rule is
            argued in patientConstants. */}
        {typeLabel && <span className={`ph-chip ${typeClass || ""}`}>{typeLabel}</span>}
        {/* Rendered even with no therapy set — the chip then reads "Set
            therapy", because a header that hides the control when it is most
            needed is the wrong trade. Desktop's reasoning, unchanged. */}
        <TherapyPicker
          patientId={patient.id || patient.patientID}
          tag={therapyTag}
          label={condition}
          onChanged={onTherapyChanged}
        />
      </div>

      <div className="zym-meta-line">
        {genderAge && <span className="zym-meta-text">{genderAge}</span>}
        {/* Only while "Update booking for" has pointed this record away from
            the member the appointment is booked for. Same condition as
            desktop; the board draws it as a chip on this line. */}
        {originalBookingName && (
          <span className="zym-chip is-flag">
            Original appointment · {originalBookingName}
          </span>
        )}
      </div>

      {MOBILE.showPreferredLanguages && (
        <div className="zym-meta-line">
          <PreferredLanguagesPopover
            patientId={patient.id}
            languages={languages}
            languagesState={languagesState}
            onSaved={onLanguagesSaved}
          />
        </div>
      )}
    </div>
  );
};

MobilePatientMeta.propTypes = {
  patient: PropTypes.object,
  condition: PropTypes.string,
  therapyTag: PropTypes.string,
  onTherapyChanged: PropTypes.func.isRequired,
  languages: PropTypes.array,
  languagesState: PropTypes.string,
  onLanguagesSaved: PropTypes.func,
  originalBookingName: PropTypes.string,
};

export default MobilePatientMeta;
